import React, { useEffect, useState, useCallback, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useInView } from "react-intersection-observer";
import Spinner from "@/shared/ui/Spinner/Spinner";
import WebOnboardingHomePage from "@/widgets/WebOnboardingHomePage/ui/PhoneHomePage/WebOnboardingHomePage";
import { getFilterAppsState } from "@/entities/filterApps";
import { getFilterInputSearchState } from "@/entities/filterInputSearch";
import { activeModalIndexActions } from "@/entities/activeModalIndex";
import { useGetAppOnboardingVideosQuery } from "@/features/auth/api/authAPI";
import { ACTIVE_VIEW_WEB_ONBOARDING } from "@/Views/constants";
import styles from "./WebOnboarding.module.scss";
import { getFilterState } from "@/entities/filter";
import { useSelectedOnboardingScreens } from "@/entities/selectedScreens";
import SelectedScreensPopup from "@/Views/Virtual/SelectedScreensPopup/SelectedScreensPopup";
import { loadingStatesActions } from "@/entities/loadingStates";

const makeOrderValue = (key: string, direction: string) => {
  if (key !== "") {
    return `${direction === "desc" ? "-" : ""}${key}`;
  } else {
    return "";
  }
};

const WebOnboarding = () => {
  const { appName } = useSelector(getFilterInputSearchState);

  const {
    filterSort,
    categories,
    oscgte,
    osclte,
    revenuegte,
    revenuelte,
    downloadgte,
    downloadlte,
    revInstallRateGte,
    revInstallRateLte,
    releasedFrom,
    releasedTo,
    updatedFrom,
    updatedTo,
    sortDirection,
    flowType,
    only,
  } = useSelector(getFilterAppsState);

  const downloadgteNumber = downloadgte === "Min" ? "" : downloadgte;
  const downloadlteNumber = downloadlte === "Max" ? "" : downloadlte;
  const revenuegteNumber = revenuegte === "Min" ? "" : revenuegte;
  const revenuelteNumber = revenuelte === "Max" ? "" : revenuelte;
  const oscgteNumber = oscgte === "Min" ? "" : oscgte;
  const osclteNumber = osclte === "Max" ? "" : osclte;
  const revInstallRateGteNumber =
    revInstallRateGte === "Min" ? "" : revInstallRateGte;
  const revInstallRateLteNumber =
    revInstallRateLte === "Max" ? "" : revInstallRateLte;

  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [filterParams, setFilterParams] = useState({
    page: 1,
    page_size: 4,
    only: only,
    categories: categories,
    name: appName,
    downloadgteNumber: downloadgteNumber,
    downloadlteNumber: downloadlteNumber,
    revenuegteNumber: revenuegteNumber,
    revenuelteNumber: revenuelteNumber,
    oscgteNumber: oscgteNumber,
    osclteNumber: osclteNumber,
    releasedFrom: releasedFrom,
    releasedTo: releasedTo,
    updatedFrom: updatedFrom,
    updatedTo: updatedTo,
    order: makeOrderValue(filterSort, sortDirection),
    revInstallRateGteNumber: revInstallRateGteNumber,
    revInstallRateLteNumber: revInstallRateLteNumber,
  });

  const [allVideos, setAllVideos] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [initialRender, setInitialRender] = useState(true);
  const [showNoResults, setShowNoResults] = useState(false);

  const { isActiveMagic } = useSelector(getFilterState);
  const { setRootActiveView } = useOutletContext();

  const { ref: loadMoreRef, inView } = useInView({
    threshold: 0,
    rootMargin: "100px",
  });

  // Add this new state to track saved/unsaved screens
  const [savedScreensMap, setSavedScreensMap] = useState<
    Record<number, { is_saved: boolean; is_saved_pk: number | null }>
  >({});

  // Add a state to force re-render of child components

  const { selectedScreens } = useSelectedOnboardingScreens();

  // Add this function to update saved status of screens
  const updateScreensSavedStatus = useCallback(
    (
      screenIds: number[],
      isSaved: boolean,
      savedPointIds: Record<number, number> | null,
    ) => {
      setSavedScreensMap((prevMap) => {
        const newMap = { ...prevMap };

        screenIds.forEach((screenId) => {
          if (isSaved && savedPointIds) {
            newMap[screenId] = {
              is_saved: true,
              is_saved_pk: savedPointIds[screenId],
            };
          } else {
            newMap[screenId] = {
              is_saved: false,
              is_saved_pk: null,
            };
          }
        });

        return newMap;
      });
    },
    [],
  );

  // Update filter parameters when any filter changes
  useEffect(() => {
    if (initialRender) {
      setInitialRender(false);
      return;
    }

    setAllVideos([]);
    setPage(1);
    setIsLoadingMore(false);

    setFilterParams({
      page: 1,
      name: appName,
      categories,
      downloadgteNumber,
      downloadlteNumber,
      revenuegteNumber,
      revenuelteNumber,
      oscgteNumber,
      osclteNumber,
      releasedFrom,
      releasedTo,
      updatedFrom,
      updatedTo,
      order: makeOrderValue(filterSort, sortDirection),
      flow_type: flowType,
      revInstallRateGte: revInstallRateGteNumber,
      revInstallRateLte: revInstallRateLteNumber,
      only: only,
    });
  }, [
    categories,
    appName,
    downloadgte,
    downloadlte,
    revenuegte,
    revenuelte,
    revInstallRateGte,
    revInstallRateLte,
    oscgte,
    osclte,
    releasedFrom,
    releasedTo,
    updatedFrom,
    updatedTo,
    filterSort,
    sortDirection,
    flowType,
    only,
  ]);

  useEffect(() => {
    setRootActiveView(ACTIVE_VIEW_WEB_ONBOARDING);
  }, []);

  useEffect(() => {
    dispatch(activeModalIndexActions.setActiveModalIndexFilterItem(1));
  }, []);

  const { data, isLoading, isFetching } = useGetAppOnboardingVideosQuery({
    ...filterParams,
  });

  // Add a debounce mechanism for the loading state
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeout
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    if (isFetching) {
      // Set loading state to true immediately when fetching starts
      dispatch(loadingStatesActions.setWebOnboardingLoading(true));
    } else {
      // Delay setting loading state to false to prevent flickering
      loadingTimeoutRef.current = setTimeout(() => {
        dispatch(loadingStatesActions.setWebOnboardingLoading(false));
      }, 300); // 300ms delay before turning off loading state
    }

    // Cleanup on unmount
    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [isFetching, dispatch]);

  // Handle infinite scroll
  useEffect(() => {
    if (inView && !isLoading && !isFetching && data?.next) {
      //setIsLoadingMore(true);
      //setPage((prev) => prev + 1);
      setFilterParams((prev) => ({
        ...prev,
        page: prev.page + 1,
      }));
    }
  }, [inView, isLoading, isFetching, isLoadingMore, data?.next]);

  // Handle data updates
  useEffect(() => {
    if (!data?.results || isLoading) return;

    if (filterParams.page === 1) {
      setAllVideos(data.results);
    } else {
      setAllVideos((prev) => [...prev, ...data.results]);
      //setIsLoadingMore(false);
    }
  }, [data, isLoading]);

  // Add this useEffect for delayed empty state display
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (!isLoading && !isFetching && allVideos.length === 0) {
      timer = setTimeout(() => {
        setShowNoResults(true);
      }, 200);
    } else {
      setShowNoResults(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isLoading, isFetching, allVideos.length]);

  const hasMore = !!data?.next;

  return (
    <div
      className={`${styles.container} ${isActiveMagic ? styles.overlay : ""}`}
    >
      <div className={`${styles.cards} ${isLoading ? styles.loading : ""}`}>
        {showNoResults ? (
          <div className={styles.noMatches}>
            <img
              src="/img/Illustrations.jpg"
              width="250px"
              height="250px"
              alt="no results"
            />

            <h2>No Results Found</h2>
            <p>
              It looks like there are no onboarding flows matching your current
              filters.
              <br />
              Try adjusting your filters
            </p>
          </div>
        ) : (
          allVideos.map((item, index) => (
            <WebOnboardingHomePage
              key={`${item.id}-${index}`}
              videoData={item}
              isOpen={false}
              savedScreensMap={savedScreensMap}
            />
          ))
        )}
      </div>
      {hasMore && (
        <div ref={loadMoreRef} className={styles.loadingContainer}>
          {(isLoading || isFetching) && <Spinner />}
        </div>
      )}
      {selectedScreens.length > 0 && (
        <SelectedScreensPopup
          type="onboardingScreens"
          updateScreensSavedStatus={updateScreensSavedStatus}
        />
      )}
    </div>
  );
};

export default WebOnboarding;
